import { useMemo } from 'react';
import { createTraditionalLTCQuoteWithROIandProjectedCoverage } from '@client/util/policyQuoteCalculations';
import { formatROI } from '@shared';

export interface TraditionalLTCQuoteWithROI extends TraditionalLTCQuote {
  roi?: number;
  formattedROI?: string;
  roiValues?: {
    projectedReturnOnInvestment: number;
    projectedCompoundAnnualGrowthRate: number;
    projectedInternalRateOfReturn: number | undefined;
    investmentPeriodYears: number;
  };
  projectedCostCoverage?: number;
  discountSavings?: number;
  discountPolicyType?: number;
}

interface UseTraditionalLTCQuotesWithROIParams {
  quotes: TraditionalLTCQuote[] | undefined;
  client: Client | undefined;
  returnOnInvestmentCalculationType: ReturnOnInvestmentCalculationType;
}

interface UseTraditionalLTCQuotesWithROIReturn {
  quotesWithROI: TraditionalLTCQuoteWithROI[];
  isCalculating: boolean;
}

export const useTraditionalLTCQuotesWithROI = ({
  quotes,
  client,
  returnOnInvestmentCalculationType,
}: UseTraditionalLTCQuotesWithROIParams): UseTraditionalLTCQuotesWithROIReturn => {
  return useMemo(() => {
    if (!quotes || !client || quotes.length === 0) {
      return {
        quotesWithROI: [],
        isCalculating: false,
      };
    }
    const quotesWithROIandProjectedCoverage = quotes.map(quote => {
      try {
        const enrichedQuote = createTraditionalLTCQuoteWithROIandProjectedCoverage(
          quote,
          client,
          returnOnInvestmentCalculationType,
        );

        const formattedROI = formatROI(enrichedQuote.roiValues, returnOnInvestmentCalculationType);

        return {
          ...enrichedQuote,
          formattedROI,
        } satisfies TraditionalLTCQuoteWithROI;
      } catch (error) {
        console.error(
          'Error calculating ROI for Traditional LTC quote:',
          quote.traditionalLTCQuoteId,
          error,
        );
        return {
          ...quote,
          roi: 0,
          roiValues: {
            projectedReturnOnInvestment: 0,
            projectedCompoundAnnualGrowthRate: 0,
            projectedInternalRateOfReturn: undefined,
            investmentPeriodYears: 0,
          },
          projectedCostCoverage: 0,
          formattedROI: '0%',
        } satisfies TraditionalLTCQuoteWithROI;
      }
    });

    const sortedQuotes = quotesWithROIandProjectedCoverage.sort(
      (a, b) => (b.roi || 0) - (a.roi || 0),
    );

    return {
      quotesWithROI: sortedQuotes,
      isCalculating: false,
    };
  }, [quotes, client, returnOnInvestmentCalculationType]);
};
